"use client";

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

const FAQContent = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  const faqs: FAQItem[] = [
    {
      id: 'faq_what_is',
      question: 'What is Legacy?',
      answer: 'Legacy is a personal digital space where you can preserve your life story, upload photos and videos, record voice messages, and write heartfelt letters — all to be shared with the people you love. Think of it as a living memory book that captures who you are, in your own words and voice, for the people who matter most to you.'
    },
    {
      id: 'faq_how_to_create',
      question: 'How do I create a Legacy?',
      answer: 'It\'s simple. Sign in, then choose what you\'d like to share — write a letter, record a video, upload photos, or add a voice message. You can build your story piece by piece over time, at your own pace. There\'s no rush; Legacy is always here when inspiration strikes.'
    },
    {
      id: 'faq_edit_messages',
      question: 'Can I update or edit my messages later?',
      answer: 'Absolutely. Your story is always yours to shape. You can return at any time to update your messages, add new memories, upload more photos, or record something fresh. Legacy grows with you.'
    },
    {
      id: 'faq_change_mind',
      question: 'What if I change my mind about sending a message?',
      answer: 'You are always in control. You can change who receives a message, adjust the timing, rewrite it, or delete it entirely. Nothing is ever delivered without your approval, and you can update your wishes at any time.'
    },
    {
      id: 'faq_what_is_messenger',
      question: 'What is a Messenger?',
      answer: 'A Messenger is a trusted person you designate to deliver your messages, memories, and legacy to your loved ones after you pass. Think of them as your personal representative — someone you trust completely to carry out your final wishes.\n\nWhen the time comes, your Messenger receives a secure notification and is given access to the messages and content you have prepared specifically for delivery. They do not see everything in your Legacy — only what you have chosen to share through them.\n\nYou can assign different Messengers to different messages, so the right person delivers the right memory to the right loved one. Your Messenger could be a close friend, a family member, a trusted colleague, or even your solicitor. The most important thing is that they are someone who will honour your wishes with care and love.'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleItems((prev) => (prev.includes(index) ? prev : [...prev, index]));
          }
        });
      },
      { threshold: 0.2 }
    );

    const faqElements = sectionRef.current?.querySelectorAll('.faq-item');
    faqElements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section ref={sectionRef} className="py-24 bg-background">
      <div className="max-w-4xl mx-auto px-6">
        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={faq.id}
              data-index={index}
              className={`faq-item bg-card rounded-3xl border border-border shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden ${
                visibleItems.includes(index)
                  ? 'opacity-100 translate-y-0' :'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Question Button */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
                aria-expanded={openIndex === index}
              >
                <h3 className="font-jakarta font-semibold text-lg md:text-xl text-foreground pr-4">
                  {faq.question}
                </h3>
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                >
                  <Icon
                    name="ChevronDownIcon"
                    size={24}
                    className="text-primary"
                  />
                </div>
              </button>

              {/* Answer */}
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-8 pb-6 pt-2">
                  <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-16 text-center">
          <div className="glass-panel rounded-3xl p-8 md:p-12">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="QuestionMarkCircleIcon" size={32} className="text-primary" />
            </div>
            <h3 className="font-jakarta font-bold text-2xl md:text-3xl text-foreground mb-4">
              Still have questions?
            </h3>
            <p className="text-muted-foreground text-lg mb-6 max-w-2xl mx-auto">
              We're here to help. Reach out to our support team and we'll get back to you as soon as possible.
            </p>
            <a
              href="mailto:support@lastfarewell.bm"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
            >
              <Icon name="EnvelopeIcon" size={20} />
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQContent;